import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Link,
  CircularProgress,
  Skeleton,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import { paths } from '../routes/paths';
import { useTermsModal } from '../contexts/TermsModalContext';
import { useApiClient } from '../ApiClientProvider';
import { useNotifications } from '@toolpad/core';

const TermsAcceptanceModal: React.FC = () => {
  const theme = useTheme();
  const { showTermsModal, termsVersions, isTermsUpdate, pendingRequest, onCancel, closeTermsModal, setTermsVersions } = useTermsModal();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const apiClient = useApiClient('managementClient');
  const notifications = useNotifications();

  useEffect(() => {
    // Fetch terms versions if modal is open but versions are not loaded
    if (showTermsModal && !termsVersions) {
      const fetchTermsVersions = async () => {
        setFetchError(false);
        try {
          const response = await apiClient.get('/users/me/terms-status');
          const { currentTermsVersion, currentSignupTermsVersion } = response.data;
          setTermsVersions({
            termsVersion: currentTermsVersion,
            signupTermsVersion: currentSignupTermsVersion,
          });
        } catch (error) {
          console.error('Failed to fetch terms versions in modal:', error);
          // Surface the failure so the user is not left with an indefinite spinner.
          // This typically means the session is no longer authenticated.
          setFetchError(true);
        }
      };

      fetchTermsVersions();
    }
  }, [showTermsModal, termsVersions, apiClient, setTermsVersions]);

  const handleClose = () => {
    // Call onCancel if provided (when user closes without accepting)
    if (onCancel && !loading) {
      onCancel();
    }
    closeTermsModal();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleAccept = async () => {
    if (!termsVersions) return;

    setLoading(true);
    try {
      // Call the new endpoint to accept terms for authenticated user
      await apiClient.post('/users/me/accept-terms', {
        acceptTerms: true,
        termsVersion: termsVersions.termsVersion,
        signupTermsVersion: termsVersions.signupTermsVersion,
        acceptedAtUtc: new Date().toISOString(),
        acceptedVia: 'web-inline',
      });

      notifications.show('Terms accepted successfully', {
        severity: 'success',
        autoHideDuration: 3000,
      });

      closeTermsModal();

      // Retry the original request if available
      if (pendingRequest) {
        await pendingRequest();
      }
    } catch (error) {
      console.error('Error accepting terms:', error);
      const message =
        (axios.isAxiosError(error) && typeof error.response?.data === 'string' && error.response.data) ||
        'Failed to accept terms. Please try again.';
      notifications.show(message, {
        severity: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Show modal even if termsVersions is null initially - it might be loading
  if (!showTermsModal) return null;

  const linkSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'primary.main',
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  } as const;

  return (
    <Dialog
      open={showTermsModal}
      onClose={() => { }} // Disable closing via X button or Escape
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true} // Prevent Escape key from closing
      slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
    >
      <DialogTitle sx={{ fontWeight: 500, letterSpacing: '-0.02em' }}>
        {isTermsUpdate ? 'Terms & Conditions Updated' : 'Accept Terms & Conditions'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          {isTermsUpdate
            ? 'We have updated our Terms & Conditions. Please review and accept to continue using AttendIn.'
            : 'To continue using AttendIn, please review and accept our Terms & Conditions.'}
        </Typography>

        {fetchError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            We could not load the latest Terms &amp; Conditions. Your session may have expired —
            please decline, sign in again, and retry.
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isTermsUpdate
              ? 'Please review our updated:'
              : 'Please review our:'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {termsVersions ? (
              <>
                <Link
                  component={RouterLink}
                  to={paths.public.termsOfServicePage}
                  target="_blank"
                  sx={linkSx}
                >
                  <ArticleIcon sx={{ fontSize: 20 }} />
                  Terms of Service (v{termsVersions.termsVersion})
                </Link>
                <Link
                  component={RouterLink}
                  to={paths.public.signupTermsPage}
                  target="_blank"
                  sx={linkSx}
                >
                  <ArticleIcon sx={{ fontSize: 20 }} />
                  Signup Terms & Conditions (v{termsVersions.signupTermsVersion})
                </Link>
              </>
            ) : !fetchError ? (
              <>
                <Skeleton variant="rounded" height={24} width="70%" />
                <Skeleton variant="rounded" height={24} width="80%" />
              </>
            ) : null}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
            By clicking "Accept & Continue" below, you acknowledge that you have read, understood, and agree to be
            bound by these terms.
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="inherit"
          disabled={loading}
          sx={{
            py: 1.25,
            flex: 1,
            fontWeight: 500,
            borderRadius: '8px',
            textTransform: 'none',
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Decline
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disableElevation
          disabled={loading || !termsVersions}
          sx={{
            py: 1.25,
            flex: 1,
            fontWeight: 500,
            borderRadius: '8px',
            textTransform: 'none',
          }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Accept & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
